import { z } from 'zod';

export const entitlementSchema = z.object({
  module: z.string(),
  tier: z.string(),
});

export const licenseSchema = z.object({
  id: z.string().uuid(),
  serial: z.string(),
  type: z.enum(['perpetual', 'subscription', 'trial', 'dev']),
  status: z.enum(['active', 'expired', 'revoked', 'pending', 'permitted']),
  expiresAt: z.string().nullable(),
  activationCount: z.number().int(),
  createdAt: z.string(),
});

export const licenseDetailSchema = licenseSchema.extend({
  maxActivations: z.number().int(),
  entitlements: z.array(entitlementSchema),
});

export const licensesListResponseSchema = z.object({
  data: z.array(licenseSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  pages: z.number().int(),
});

export const entitlementOptionSchema = z.object({
  module: z.string().min(1).max(64),
  tier: z.string().min(1).max(32),
});

export const createLicensePayloadSchema = z.object({
  serial: z.string().min(1, 'Serial is required'),
  type: z.enum(['perpetual', 'subscription', 'trial', 'dev'], {
    required_error: 'Type is required',
  }),
  maxActivations: z.number().int().min(1).max(1000).default(1),
  expiresAt: z.string().max(64).optional(),
  entitlements: z.array(entitlementOptionSchema).default([]),
});

export const auditEventSchema = z.object({
  id: z.string(),
  timestamp: z.string(),
  event: z.string(),
  serial: z.string(),
  licenseId: z.string(),
  performedBy: z.string(),
  ipAddress: z.string().nullable(),
  metadata: z.record(z.unknown()).optional(),
});

export const licenseAuditSchema = z.object({
  data: z.array(auditEventSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  pages: z.number().int(),
});

export type License = z.infer<typeof licenseSchema>;
export type LicenseDetail = z.infer<typeof licenseDetailSchema>;
export type Entitlement = z.infer<typeof entitlementSchema>;
export type AuditEvent = z.infer<typeof auditEventSchema>;
export type LicenseAuditResponse = z.infer<typeof licenseAuditSchema>;
export type LicensesListResponse = z.infer<typeof licensesListResponseSchema>;
export type CreateLicensePayload = z.infer<typeof createLicensePayloadSchema>;
export type EntitlementOption = z.infer<typeof entitlementOptionSchema>;
